import { getTaskTitle } from '../../utils/taskUtils'
import { type Requisition, type Sample, Task } from '../../types/types'
import { CheckCircleIcon, Mtds, SubstanceTags } from 'prove-common'
import { type ReactNode } from 'react'
import { Link } from 'react-router'
import { useGetPlanByIdQuery } from '../../hooks/usePlan'
import { useGetOkProgramsQuery } from '../../hooks/useOkProgram'
import { useGetPurposesQuery } from '../../hooks/usePurpose'
import { useGetSubPurposesQuery } from '../../hooks/useSubPurpose'

type SampleDetailsSectionProps = {
  task?: Task
  sample?: Sample
  requisition?: Requisition
  isLoadingTask?: boolean
  taskLinkTo?: string
  isProveta: boolean
  updated?: Sample
}

export const SampleDetailsSection = ({
  task,
  sample,
  requisition,
  isLoadingTask,
  taskLinkTo,
  isProveta,
  updated
}: SampleDetailsSectionProps) => {
  const { data: plan, isLoading: isLoadingPlan } = useGetPlanByIdQuery(
    sample?.planId
  )
  const { data: okPrograms } = useGetOkProgramsQuery(
    sample?.okProgramId != null
  )
  const okProgram = okPrograms?.find((p) => p.id === sample?.okProgramId)
  const { data: purposes } = useGetPurposesQuery(plan?.purposeId != null)
  const { data: subPurposes } = useGetSubPurposesQuery(
    task?.subPurposeId != null
  )
  const purpose = purposes?.find((p) => p.id === plan?.purposeId)
  const subPurpose = subPurposes?.find((p) => p.id === task?.subPurposeId)
  let taskContent: ReactNode = '-'
  if (isLoadingTask) {
    taskContent = <Mtds.Skeleton />
  } else if (task) {
    taskContent = <Link to={taskLinkTo || '#'}>{getTaskTitle(task)}</Link>
  }

  let planContent: ReactNode = '-'
  if (isLoadingPlan) {
    planContent = <Mtds.Skeleton />
  } else if (plan) {
    planContent = isProveta ? (
      plan.description
    ) : (
      <Link to={`/plans/${plan.id}?tab=2`}>{plan.description}</Link>
    )
  }

  const okProgramValue =
    okProgram == null ? (
      '-'
    ) : (
      <>
        <strong>{okProgram.number}</strong> {okProgram.name}
      </>
    )
  const purposeValue = purpose ? (
    <Mtds.Flex data-align='center' data-gap='2'>
      {purpose.name}
      {plan && !isProveta ? (
        <Link
          to={`/plans/${plan.id}?tab=2`}
          aria-label={`Gå til plan ${plan.description ?? ''}`.trim()}
        >
          <Mtds.Tag data-color='neutral' data-icon='none'>
            Plan
          </Mtds.Tag>
        </Link>
      ) : (
        <Mtds.Tag data-color='neutral' data-icon='none'>
          Plan
        </Mtds.Tag>
      )}
    </Mtds.Flex>
  ) : (
    '-'
  )
  const subPurposeValue = subPurpose?.name ?? '-'

  return (
    <Mtds.Grid data-gap='4'>
      <Mtds.Heading>Detaljer</Mtds.Heading>
      <Mtds.Table data-fixed>
        <tbody>
          <tr>
            <th>Produkt</th>
            <td>
              <Mtds.Flex data-justify={'space-between'} data-align={'center'}>
                {sample?.sampleType.product ?? '-'}
                {(!!updated?.sampleType?.product ||
                  !!updated?.sampleType?.matrix) && <CheckCircleIcon />}
              </Mtds.Flex>
            </td>
          </tr>
          <tr>
            <th>Prøve av</th>
            <td>
              <Mtds.Flex data-justify={'space-between'} data-align={'center'}>
                <strong>{sample?.sampleType.matrix ?? '-'}</strong>
                {(!!updated?.sampleType?.product ||
                  !!updated?.sampleType?.matrix) && <CheckCircleIcon />}
              </Mtds.Flex>
            </td>
          </tr>
          <tr>
            <th>Analyser for</th>
            <td>
              <Mtds.Flex data-justify={'space-between'} data-align={'center'}>
                <SubstanceTags analysisType={requisition?.analysisType} />
                {!!updated?.requisitions?.[0]?.analysisType && (
                  <CheckCircleIcon />
                )}
              </Mtds.Flex>
            </td>
          </tr>
          <tr>
            <th>Laboratorium</th>
            <td>
              <Mtds.Flex data-justify={'space-between'} data-align={'center'}>
                {requisition?.lab.name}
                {!!updated?.requisitions?.[0]?.lab?.name && <CheckCircleIcon />}
              </Mtds.Flex>
            </td>
          </tr>
          <tr>
            <th>Uttakssted</th>
            <td>
              <Mtds.Flex data-justify={'space-between'} data-align={'center'}>
                {sample?.sampntType?.nameNo}
                {!!updated?.sampntType?.nameNo && <CheckCircleIcon />}
              </Mtds.Flex>
            </td>
          </tr>
          <tr>
            <th>Arkiv</th>
            <td>
              <Mtds.Flex data-justify={'space-between'} data-align={'center'}>
                {sample?.archiveId ?? 'Venter på arkiv'}
                {!!updated?.archiveId && <CheckCircleIcon />}
              </Mtds.Flex>
            </td>
          </tr>
          <tr>
            <th>Oppgave</th>
            <td>
              <Mtds.Flex data-justify={'space-between'} data-align={'center'}>
                {taskContent}
                {!!updated?.taskId && <CheckCircleIcon />}
              </Mtds.Flex>
            </td>
          </tr>
          <tr>
            <th>Plan</th>
            <td>{planContent}</td>
          </tr>
          <tr>
            <th>Hensikt</th>
            <td>{purposeValue}</td>
          </tr>
          <tr>
            <th>OK-program</th>
            <td>{okProgramValue}</td>
          </tr>
          <tr>
            <th>Underhensikt</th>
            <td>{subPurposeValue}</td>
          </tr>
        </tbody>
      </Mtds.Table>
    </Mtds.Grid>
  )
}
